import {useEffect} from 'react';
import {useRouter} from 'next/router';
import {useIntl} from 'react-intl';
import {useQueryClient} from '@tanstack/react-query';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Flex,
  Text,
  ButtonProps,
  Center,
  FlexProps,
  useBoolean,
  ModalProps,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {ArrowDownCircleIcon, BackIcon, CheckIcon, ListDoneIcon} from '@eksab/assets/icons';
import {useMeQuery} from '@eksab/hooks';
import {getLanguage} from '@eksab/util';

import {BuyCoinsModal} from './BuyCoinsModal';
import {PlaygroundLineup} from '../playground-lineup';
import {addUIState, Substitutes} from '../../lineup';
import {useLineupStore} from '../../lineup/useLineupStore';
import {useSubmitLineupMutation} from '../../lineup/useSubmitLineupMutation';
import {CheckSameLineupQueryResponse, useCompetitionJoin, useCompetitionQuery} from '../../hooks';
import {parsePlayers} from '../../helpers';
import type {TeamPlayer} from '../../types';

interface ApplySameLineupModalProps extends Omit<ModalProps, 'children'> {
  lineup: CheckSameLineupQueryResponse['team_setup'] | undefined;
}

export function ApplySameLineupModal({lineup, ...props}: ApplySameLineupModalProps) {
  const format = useIntl().formatNumber;
  const competitionID = useRouter().query.slug as string;
  const queryClient = useQueryClient();
  const toast = useToast();
  const [viewLineup, {toggle: toggleLineup}] = useBoolean();
  const buyCoinsModalDisclosure = useDisclosure();

  useEffect(() => {
    if (props.isOpen) mixpanel?.track('Submit Same Lineup Modal Opened');
  }, [props.isOpen]);

  const t = useTranslate();
  const {data: me} = useMeQuery();
  const {data: competition} = useCompetitionQuery(competitionID);
  const [setLineupStore, resetLineupStore] = useLineupStore((state) => [state.setState, state.reset]);
  const joinMutation = useCompetitionJoin();
  const submitLineupMutation = useSubmitLineupMutation(competitionID, {
    onSuccess: () =>
      toast({
        status: 'success',
        title: t('fantasy.lineup.multi-entry.success', {competitions: <em>{competitionID}</em>}),
      }),
    onError: (e) => toast({status: 'error', title: e.response?.data?.message ?? t('app.errors.try-again')}),
  });

  const [lineupPlayers, substitutePlayers] = lineup ? parsePlayers(lineup.players, lineup.formation) : [];

  const formatFormation = (formation: Formation) =>
    formation
      .split('-')
      .map((num) => format(+num))
      .join('-');

  const fillLineupUI = () => {
    setLineupStore({
      players: lineupPlayers!,
      formation: lineup!.formation,
      substitutes: substitutePlayers!,
      numberOfPlayers: 15,
      overallCost: lineup!.players.reduce((totalCost, {price}) => totalCost + price, 0),
      indicatorPosition: {position: 'Goalkeeper', index: 0},
    });

    queryClient.setQueriesData(
      {
        predicate: ({queryKey}) => queryKey[0] === 'team-players',
      },
      (players) => (players ? addUIState(players as TeamPlayer[]) : players),
    );
  };

  const onEdit = () => {
    fillLineupUI();
    props.onClose();
  };

  const submitLineup = () => {
    mixpanel?.track('Button Clicked', {button: 'submit_same_lineup'});
    submitLineupMutation.mutate({
      players: lineupPlayers!,
      substitutes: substitutePlayers!,
      formation: lineup?.formation!,
    });
  };

  const onApplyLineup = () => {
    if (competition?.entry_fee! > me?.coins!) return buyCoinsModalDisclosure.onOpen();

    fillLineupUI();

    if (!competition?.joined)
      return joinMutation.mutate(competitionID, {
        onSuccess: () => {
          toast({title: t('competition.join.success'), status: 'success'});

          submitLineup();
          props.onClose();
        },
        onError: (e) => toast({title: e.response?.data.message ?? t('competition.join.error'), status: 'error'}),
      });

    submitLineup();
    props.onClose();
  };

  const onSkip = () => {
    resetLineupStore();
    props.onClose();
  };

  return lineup ? (
    <Modal isCentered closeOnOverlayClick={false} size="md" {...props}>
      <ModalOverlay />
      <ModalContent
        pos="relative"
        borderWidth="1px"
        borderColor="#dfdfdf"
        rounded="3xl"
        maxH={['90vh', null, '80vh']}
        overflow="auto"
      >
        <ModalHeader textAlign="center">
          <ListDoneIcon />

          <Text as="h2" fontSize={['xl', null, '2xl']} fontWeight="700" my="2">
            {t('fantasy.lineup.apply-same.title')}
          </Text>

          <Text fontSize="md" fontWeight="500" color="#919191" mt="2">
            {t('fantasy.lineup.apply-same.description')}
          </Text>
        </ModalHeader>

        <ModalBody display="flex" flexDir="column" gap="4" py="0">
          <ToggleLineupButton isOpen={viewLineup} onClick={toggleLineup} alignSelf="center" />

          {viewLineup && (
            <Flex flexDir="column" gap="4">
              <Flex align="center" justify="space-between">
                <Center
                  px="4"
                  py="2"
                  borderWidth="1px"
                  borderColor="green.400"
                  bg="#e2fff1"
                  fontWeight="800"
                  rounded="full"
                >
                  {formatFormation(lineup.formation)}
                </Center>

                <EditLineupButton onClick={onEdit} />
              </Flex>

              <Flex flexDir="column" gap="3">
                <PlaygroundLineup players={lineupPlayers} showStatsModal={false} />

                <Flex gap="2" px="2" py="5" bg="gray.600" rounded="md">
                  <Substitutes substitutes={substitutePlayers} showStatsModal={false} />
                </Flex>
              </Flex>
            </Flex>
          )}
        </ModalBody>

        <ModalFooter flexDir="column" gap="4" pos="sticky" bottom="0" bg="white" zIndex="10">
          <ApplyLineupButton
            isLoading={submitLineupMutation.isLoading}
            isDisabled={submitLineupMutation.isLoading}
            onClick={onApplyLineup}
          />

          <Button variant="link" fontWeight="600" color="#ABABAB" textDecor="underline" onClick={onSkip}>
            {t('app.actions.skip')}
          </Button>
        </ModalFooter>
      </ModalContent>

      <BuyCoinsModal {...buyCoinsModalDisclosure} fees={competition?.entry_fee ?? 0} />
    </Modal>
  ) : null;
}

interface ToggleButton extends ButtonProps {
  isOpen: boolean;
}

const ToggleLineupButton = ({isOpen, ...props}: ToggleButton) => {
  const t = useTranslate();

  return (
    <Button
      variant="ghost"
      gap="2.5"
      w="full"
      fontSize="lg"
      fontWeight="600"
      px="2"
      py="3"
      rounded="full"
      bg={isOpen ? '#DEF3E5' : 'white'}
      borderWidth="1px"
      borderColor={isOpen ? 'currentcolor' : '#dfdfdf'}
      color={isOpen ? 'green.400' : '#2a2e3a'}
      transition="border-color 0.5s, color 0.5s, background 0.5s"
      {...props}
    >
      {t(`app.actions.${isOpen ? 'hide' : 'show'}`)} {t('fantasy.lineup')}
      <ArrowDownCircleIcon
        boxSize="3.5"
        transform={`rotate(${isOpen ? '0.5turn' : '1turn'})`}
        transition="transform 0.3s"
        color="currentColor"
      />
    </Button>
  );
};

const EditLineupButton = (props: FlexProps) => {
  const {locale} = useRouter();

  const t = useTranslate();

  return (
    <Flex
      as="button"
      gap="3"
      align="center"
      px="3"
      py="1.5"
      borderWidth="1px"
      borderColor="gray.400"
      bg="gray.600"
      rounded="full"
      fontWeight="700"
      {...props}
    >
      {t('app.actions.edit')}

      <BackIcon
        zIndex="2"
        transform="auto"
        scaleX={getLanguage(locale) === 'en' ? -1 : 1}
        width="3.5"
        strokeWidth="2"
      />
    </Flex>
  );
};

const ApplyLineupButton = (props: ButtonProps) => {
  const t = useTranslate();

  return (
    <Button
      gap="3"
      w="full"
      h="3.5rem"
      textTransform="capitalize"
      fontSize="lg"
      fontWeight="700"
      bgColor="unset"
      bg="linear-gradient(93deg, #f3189b, #fB6b02 88%)"
      bgSize="100%"
      transition="background-size 0.7s, background-position 0.5s"
      color="white"
      _active={{bgSize: '200%', bgPos: 'left'}}
      _hover={{bgSize: '150%', bgPos: 'right'}}
      {...props}
    >
      {t('app.actions.apply')} {t('fantasy.lineup')}
      <CheckIcon />
    </Button>
  );
};
